import type { EditorView } from "@codemirror/view";
import type { LanguageId } from "@ide/shared";

type PrettierPlugin = { default?: unknown };

/** Parser e plugins do Prettier por linguagem (carregados só quando alguém formata). */
const FORMATTERS: Partial<Record<LanguageId, { parser: string; plugins: () => Promise<PrettierPlugin[]> }>> =
  {
    javascript: {
      parser: "babel",
      plugins: () => Promise.all([import("prettier/plugins/babel"), import("prettier/plugins/estree")]),
    },
    jsx: {
      parser: "babel",
      plugins: () => Promise.all([import("prettier/plugins/babel"), import("prettier/plugins/estree")]),
    },
    typescript: {
      parser: "typescript",
      plugins: () => Promise.all([import("prettier/plugins/typescript"), import("prettier/plugins/estree")]),
    },
    tsx: {
      parser: "typescript",
      plugins: () => Promise.all([import("prettier/plugins/typescript"), import("prettier/plugins/estree")]),
    },
    json: {
      parser: "json",
      plugins: () => Promise.all([import("prettier/plugins/babel"), import("prettier/plugins/estree")]),
    },
    css: { parser: "css", plugins: () => Promise.all([import("prettier/plugins/postcss")]) },
    markdown: { parser: "markdown", plugins: () => Promise.all([import("prettier/plugins/markdown")]) },
    yaml: { parser: "yaml", plugins: () => Promise.all([import("prettier/plugins/yaml")]) },
    html: {
      parser: "html",
      plugins: () =>
        Promise.all([
          import("prettier/plugins/html"),
          import("prettier/plugins/postcss"),
          import("prettier/plugins/babel"),
          import("prettier/plugins/estree"),
        ]),
    },
  };

export const canFormat = (language: LanguageId) => language in FORMATTERS;

/**
 * Menor edição que transforma `before` em `after` (prefixo e sufixo comuns preservados).
 * No texto compartilhado, isso evita que o cursor de quem está programando junto pule.
 */
export function minimalChange(before: string, after: string) {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let endA = before.length;
  let endB = after.length;
  while (endA > start && endB > start && before[endA - 1] === after[endB - 1]) {
    endA--;
    endB--;
  }
  return start === endA && start === endB
    ? null
    : { from: start, to: endA, insert: after.slice(start, endB) };
}

/**
 * Formatação básica, para as linguagens sem Prettier (Python, Java, Go, SQL…): tira espaços no fim
 * das linhas, junta linhas em branco repetidas e deixa uma quebra de linha no fim. Não mexe na
 * indentação (em Python ela é parte do código).
 */
export function basicFormat(source: string): string {
  const text = source
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\s+$/, "");
  return text ? `${text}\n` : "";
}

/** Formata o documento do editor. Devolve false se já estava formatado. */
export async function formatDocument(view: EditorView, language: LanguageId): Promise<boolean> {
  const formatter = FORMATTERS[language];
  const source = view.state.doc.toString();
  let formatted: string;
  if (formatter) {
    const [{ format }, plugins] = await Promise.all([import("prettier/standalone"), formatter.plugins()]);
    formatted = await format(source, {
      parser: formatter.parser,
      plugins: plugins.map((p) => (p.default ?? p) as never),
      printWidth: 100,
    });
  } else formatted = basicFormat(source);
  // O documento pode ter mudado enquanto o Prettier rodava (outra pessoa digitando): desiste.
  if (view.state.doc.toString() !== source) return false;
  const change = minimalChange(source, formatted);
  if (!change) return false;
  view.dispatch({ changes: change, userEvent: "format" });
  return true;
}
